import React from "react";
import SearchWidgetTabs from "../searchwidgetTabs/searchWidgetTabs";
import DateTime from "src/utils/temporalLuxonUtil";
import { ApplicationContext } from "src/context";
import Icon from "src/components/icon";
import CTAButton from "src/components/ctabutton";
import { DatePickerV2 } from "src/components/datePicker";
import GuestPickerV2 from "src/components/guestPicker.v2";
import { toFormat, toYYYYMMDD } from "src/utils/datefns";

type StayProps = {
    adults: number | undefined;
    children: number | undefined;
    ages: number[] | undefined;
    startDate: Date | undefined;
    endDate: Date | undefined;
    setStartDate: (date: Date) => void;
    setEndDate: (date: Date) => void;
    setAdults: (adults: number) => void;
    setChilds: (childs: number, ages: number[]) => void;
    minimumDate: Date;
    maximumDate: Date;
    selectedLanguage: string;
    defaultLanguage: string;
    linkto: string;
}


class StayWidget extends React.Component<StayProps> {
    /** context: ApplicationContext */
    static contextType = ApplicationContext;
    /** @ts-ignore */
    context!: React.ContextType<typeof ApplicationContext>;


    get link() {
        return this.props.linkto
            .replaceAll('{{arrival}}', this.startDate)
            .replaceAll('{{departure}}', this.endDate)
            .replaceAll('{{adults}}', (this.props.adults || 1).toString())
            .replaceAll('{{children}}', (this.props.children || 0).toString())
            .replaceAll('{{startDate}}', this.startDateAlt)
            .replaceAll('{{endDate}}', this.endDateAlt);
    }

    get startDateAlt() {
        const date = this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return encodeURIComponent(toFormat(date, 'MM/DD/YYYY'));
    }

    get startDate() {
        const date = this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return toYYYYMMDD(date);
    }

    get endDateAlt() {
        const date = this.props.endDate || this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return encodeURIComponent(toFormat(date, 'MM/DD/YYYY'));
    }

    get endDate() {
        const date = this.props.endDate || this.props.startDate || (new Date() > this.props.minimumDate ? new Date() : this.props.minimumDate);
        return toYYYYMMDD(date);
    }

    render() {
        const {startDate, setStartDate, endDate, setEndDate, adults, children, ages, setAdults, setChilds} = this.props;

        return <>
        <div className={`flex flex-col lg:flex-row justify-between items-center gap-4 lg:gap-0 p-4 sm:p-6 rounded-b-2xl`}>
            <div className="w-full lg:w-1/3 lg:px-2 lg:pr-0">
                <React.Suspense fallback={<div className="w-full h-10" />}>
                    <DatePickerV2
                        isRange={true}
                        onChange={(dates: Date[])=>{ setEndDate(dates[1]); setStartDate(dates[0]); }}
                        startDate={startDate}
                        startLabel="Check in"
                        endDate={endDate}
                        endLabel="Check out"
                        openToDate={startDate}
                        includeDateIntervals={[{ start: this.props.minimumDate, end: this.props.maximumDate }]}
                        inputBackgroundColor="#2A9BDA"
                        inputPlaceholderColor="#fff"
                        inputChevronColor="#fff"
                        inputTextColor="#fff"
                        inputBorderColor="#fff"
                    />
              </React.Suspense>
            </div>
            <div className="w-full lg:w-1/3 lg:px-2">
                <GuestPickerV2
                    language={this.props.selectedLanguage}
                    withChildAge={true}
                    adults={adults}
                    children={children}
                    ages={ages}
                    setAdults={setAdults}
                    setChilds={setChilds}
                    inputColor="#ffffff"
                    backgroundColor="#2A9BDA"
                    borderColor="#ffffff"
                />
            </div>
            <div className="w-full lg:w-1/3">
                { this.context.currency
                    ? <CTAButton roundType="rounded" link={this.link} color="#2A9BDA" hovercolor="#2A9BDA" hoverbgcolor='#FFF' bgcolor='#FFF' type="primary" text="Book Now" icon={<Icon className="w-6 h-4" icon="ArrowRight" />} />
                    : <CTAButton roundType="rounded" link={this.link} color="#2A9BDA" hovercolor="#2A9BDA" hoverbgcolor='#FFF' bgcolor='#FFF' type="primary" text="Book Now" icon={<Icon className="w-6 h-4" icon="ArrowRight" />} />
                }
            </div>
        </div>
        </>
    }
}


type SearchWidgetProps = {
    selectedLanguage: string;
    defaultLanguage: string;
    removeMargin: boolean;
}

type SearchWidgetState = {
    tab: "wildpasses" | "rides" | "tickets" | "stay" | "addons";
    startDate: Date | undefined;
    endDate: Date | undefined;
    adults: number | undefined;
    children: number | undefined;
    ages: number[] | undefined;
}

export default class PanAmericanSearchWidget extends React.Component<SearchWidgetProps, SearchWidgetState> {

    static minimumDate() {
        const baseDate = new Date();
        const hour = baseDate.getHours();
        const today = DateTime.fromJSDate(baseDate).startOf("day").toJSDate();
        const tomorrow = DateTime.fromJSDate(baseDate).startOf("day").plus({ day: 1 }).startOf("day").toJSDate();
        const minimumStart = hour >= 15 ? tomorrow : today;
        return minimumStart;
    }

    static getEndDate() {
        return DateTime.fromJSDate(PanAmericanSearchWidget.minimumDate()).plus({day: 1}).toJSDate()
    }

    static maximumDate() {
        const baseDate = new Date();
        const nextYears = DateTime.fromJSDate(baseDate).startOf("day").plus({ year: 2 }).startOf("day").toJSDate();
        return nextYears;
    }

    state: SearchWidgetState = {
        tab: "stay",
        startDate: undefined,
        endDate: undefined,
        adults: 1,
        children: 0,
        ages: undefined,
    }

    setStartDate = (date: Date) => {
        const ts = date.getTime();
        if (this.state.endDate) {
            const ets = this.state.endDate.getTime();
            if (ts > ets) {
                const plus1 = DateTime.fromJSDate(date).plus({day: 1}).toJSDate()
                this.setState({startDate: date, endDate: plus1});
            }
        }
        this.setState({startDate: date});
    }

    setEndDate = (date: Date) => this.setState({endDate: date})
    setAdults = (adults: number) => this.setState({adults})
    setChilds = (childs: number, ages: number[]) => this.setState({children: childs, ages})

    render() {
        return <>
        <div className={`${this.props.removeMargin ? "" : "mt-[-88px]"} flex w-full justify-center lg:px-10 relative z-[2] flex-col`}>
            <div className="w-[95%] sm:max-w-[400px] lg:max-w-6xl justify-center m-auto flex flex-col">
                <SearchWidgetTabs
                    initialTab={2}
                    activeTabLineColor="#FFF"
                    activeFocusTextColor="#242426"
                    activeFocusBackgroundColor="#d0940f"
                    desktopActiveTabTextColor="#ffffff"
                    desktopActiveTabBackgroundColor="#2A9BDA"
                    desktopInactiveTabTextColor="#ffffff"
                    desktopInactiveTabBackgroundColor="#53B0E4D9"
                    mobileActiveTabBackgroundColor="#2A9BDA"
                    mobileActiveTabTextColor="#ffffff"
                    mobileInactiveTabBackgroundColor="#1d83bc"
                    mobileInactiveTabTextColor="#fff"
                    desktopBackgroundColor="#2A9BDA"
                    mobileBackgroundColor="#2A9BDA"
                >
                    <SearchWidgetTabs.Tab
                        label="Blue Palms"
                        icon={<Icon width={26} height={26} icon="Bed" />}
                        desktopLabelClassname="searchWidgetText text-center" mobileLabelClassname="searchWidgetText"
                    >
                        <StayWidget
                            linkto="https://irm.moreyspiers.com/irmng/?resort=01&target=availability&arrival={{arrival}}&departure={{departure}}&people1={{adults}}&people2={{children}}&_gl=1*jhsdy6*_ga*NTI4NjEyNTY4LjE3MDExNjM3OTQ.*_ga_6RZE5FY6V7*MTcwOTU1NDc5Mi43Mi4xLjE3MDk1NTUxNjEuNTQuMC4w"
                            startDate={this.state.startDate}
                            endDate={this.state.endDate}
                            setStartDate={this.setStartDate}
                            setEndDate={this.setEndDate}
                            adults={this.state.adults}
                            setAdults={this.setAdults}
                            children={this.state.children}
                            setChilds={this.setChilds}
                            ages={this.state.ages}
                            minimumDate={PanAmericanSearchWidget.minimumDate()}
                            maximumDate={PanAmericanSearchWidget.maximumDate()}
                            selectedLanguage={this.props.selectedLanguage}
                            defaultLanguage={this.props.defaultLanguage}
                        />
                    </SearchWidgetTabs.Tab>
                    <SearchWidgetTabs.Tab
                        label="Starlux"
                        icon={<Icon width={26} height={26} icon="Bed" />}
                        desktopLabelClassname="searchWidgetText text-center" mobileLabelClassname="searchWidgetText"
                    >
                        <StayWidget
                            linkto="https://irm.moreyspiers.com/irmng/?resort=05&target=availability&arrival={{arrival}}&departure={{departure}}&people1={{adults}}&people2={{children}}&_gl=1*l56wt2*_ga*NTI4NjEyNTY4LjE3MDExNjM3OTQ.*_ga_6RZE5FY6V7*MTcwOTU1NDc5Mi43Mi4xLjE3MDk1NTUxNTUuNjAuMC4w"
                            startDate={this.state.startDate}
                            endDate={this.state.endDate}
                            setStartDate={this.setStartDate}
                            setEndDate={this.setEndDate}
                            adults={this.state.adults}
                            setAdults={this.setAdults}
                            children={this.state.children}
                            setChilds={this.setChilds}
                            ages={this.state.ages}
                            minimumDate={PanAmericanSearchWidget.minimumDate()}
                            maximumDate={PanAmericanSearchWidget.maximumDate()}
                            selectedLanguage={this.props.selectedLanguage}
                            defaultLanguage={this.props.defaultLanguage}
                        />
                    </SearchWidgetTabs.Tab>
                    <SearchWidgetTabs.Tab
                        label="Pan American"
                        icon={<Icon width={26} height={26} icon="Bed" />}
                        desktopLabelClassname="searchWidgetText text-center" mobileLabelClassname="searchWidgetText"
                    >
                        <StayWidget
                            linkto="https://irm.moreyspiers.com/irmng/?resort=03&target=availability&arrival={{arrival}}&departure={{departure}}&people1={{adults}}&people2={{children}}&_gl=1*1f0acp5*_ga*NTI4NjEyNTY4LjE3MDExNjM3OTQ.*_ga_6RZE5FY6V7*MTcwOTU1NDc5Mi43Mi4xLjE3MDk1NTUxMzAuMTAuMC4w"
                            startDate={this.state.startDate}
                            endDate={this.state.endDate}
                            setStartDate={this.setStartDate}
                            setEndDate={this.setEndDate}
                            adults={this.state.adults}
                            setAdults={this.setAdults}
                            children={this.state.children}
                            setChilds={this.setChilds}
                            ages={this.state.ages}
                            minimumDate={PanAmericanSearchWidget.minimumDate()}
                            maximumDate={PanAmericanSearchWidget.maximumDate()}
                            selectedLanguage={this.props.selectedLanguage}
                            defaultLanguage={this.props.defaultLanguage}
                        />
                    </SearchWidgetTabs.Tab>
                    <SearchWidgetTabs.Tab
                        label="Port Royal"
                        icon={<Icon width={26} height={26} icon="Bed" />}
                        desktopLabelClassname="searchWidgetText text-center" mobileLabelClassname="searchWidgetText"
                    >
                        <StayWidget
                            linkto="https://irm.moreyspiers.com/irmng/?Resort=04&target=availability&arrival={{arrival}}&departure={{departure}}&people1={{adults}}&people2={{children}}&_gl=1*5nutzj*_ga*NTI4NjEyNTY4LjE3MDExNjM3OTQ.*_ga_6RZE5FY6V7*MTcwOTU1NDc5Mi43Mi4xLjE3MDk1NTUwODAuNjAuMC4w"
                            startDate={this.state.startDate}
                            endDate={this.state.endDate}
                            setStartDate={this.setStartDate}
                            setEndDate={this.setEndDate}
                            adults={this.state.adults}
                            setAdults={this.setAdults}
                            children={this.state.children}
                            setChilds={this.setChilds}
                            ages={this.state.ages}
                            minimumDate={PanAmericanSearchWidget.minimumDate()}
                            maximumDate={PanAmericanSearchWidget.maximumDate()}
                            selectedLanguage={this.props.selectedLanguage}
                            defaultLanguage={this.props.defaultLanguage}
                        />
                    </SearchWidgetTabs.Tab>
                </SearchWidgetTabs>
            </div>
        </div>
        </>
    }

}